import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Popover, Stack, Typography } from "@mui/material";
import { Chip, TextField } from "@outpave/ds";
import { focusAdjacent } from "utils/focus";

export type AmountRange = {
  min?: number;
  max?: number;
};

type AmountRangeChipProps = {
  label: string;
  value: AmountRange;
  onChange: (next: AmountRange) => void;
};

const EMPTY: AmountRange = {};

// Strip the browser's number-input spin buttons (the up/down arrows that
// appear on hover / focus). Applied to TextField's `sx`, which forwards to
// the inner input element.
const hideSpinnerSx = {
  "& input[type=number]": { MozAppearance: "textfield" },
  "& input[type=number]::-webkit-outer-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
  "& input[type=number]::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
} as const;

const hasValue = (r: AmountRange) => r.min !== undefined || r.max !== undefined;

const formatMoney = (n: number) =>
  n.toLocaleString(undefined, { style: "currency", currency: "USD" });

const summarize = (r: AmountRange): string => {
  if (r.min !== undefined && r.max !== undefined) {
    return `${formatMoney(r.min)} – ${formatMoney(r.max)}`;
  }
  if (r.min !== undefined) return `≥ ${formatMoney(r.min)}`;
  if (r.max !== undefined) return `≤ ${formatMoney(r.max)}`;
  return "";
};

// Parse the user's draft input. Empty strings clear the side; anything that
// doesn't parse to a finite number is treated as "unset" so the popover
// never commits NaN.
const parseDraft = (raw: string): number | undefined => {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : undefined;
};

const AmountRangeChip: React.FC<AmountRangeChipProps> = ({
  label,
  value,
  onChange,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [minDraft, setMinDraft] = useState("");
  const [maxDraft, setMaxDraft] = useState("");
  const popoverRef = useRef<HTMLDivElement | null>(null);

  const open = Boolean(anchorEl);

  // Reset drafts to the committed value each time the popover opens.
  useEffect(() => {
    if (open) {
      setMinDraft(value.min !== undefined ? String(value.min) : "");
      setMaxDraft(value.max !== undefined ? String(value.max) : "");
    }
  }, [open, value.min, value.max]);

  const selected = hasValue(value);
  const chipLabel = selected ? `${label} · ${summarize(value)}` : label;

  const apply = () => {
    onChange({ min: parseDraft(minDraft), max: parseDraft(maxDraft) });
    setAnchorEl(null);
  };

  const clear = () => {
    onChange(EMPTY);
    setAnchorEl(null);
  };

  return (
    <>
      <Chip
        label={chipLabel}
        trailing="chevron"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        onClear={selected ? () => onChange(EMPTY) : undefined}
        // Raw Chip, so it does not name itself the way SelectFilter does.
        aria-label={label}
      />
      <Popover
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            ref: popoverRef,
            sx: {
              borderRadius: "var(--radius-2xl)",
              border: "1px solid var(--color-border)",
              mt: "var(--space-2)",
              padding: "var(--space-5)",
              minWidth: 320,
            },
          },
        }}
      >
        <Stack
          // Tabbing past either end of the popover closes it and moves focus outside.
          onKeyDown={(e) => {
            if (e.key !== "Tab") return;
            const paper = popoverRef.current;
            if (!paper) return;
            const focusables = Array.from(
              paper.querySelectorAll<HTMLElement>(
                'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
              ),
            );
            if (focusables.length === 0) return;
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            const active = document.activeElement as HTMLElement | null;
            const leavingForward = !e.shiftKey && active === last;
            const leavingBackward = e.shiftKey && active === first;
            if (!leavingForward && !leavingBackward) return;
            e.preventDefault();
            const anchor = anchorEl;
            setAnchorEl(null);
            requestAnimationFrame(() =>
              focusAdjacent(anchor, e.shiftKey ? "backward" : "forward"),
            );
          }}
          sx={{
            gap: "var(--space-5)",
          }}
        >
          <Stack
            direction="row"
            sx={{
              gap: "var(--space-4)",
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TextField
                label={t("Min")}
                leading={<Typography component="span">$</Typography>}
                type="number"
                inputMode="decimal"
                placeholder="0"
                value={minDraft}
                onChange={(e) => setMinDraft(e.target.value)}
                fullWidth
                sx={hideSpinnerSx}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TextField
                label={t("Max")}
                leading={<Typography component="span">$</Typography>}
                type="number"
                inputMode="decimal"
                placeholder={t("No max")}
                value={maxDraft}
                onChange={(e) => setMaxDraft(e.target.value)}
                fullWidth
                sx={hideSpinnerSx}
              />
            </Box>
          </Stack>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "var(--space-3)",
            }}
          >
            <Button variant="primary-soft" onClick={clear} disabled={!selected}>
              {t("Clear")}
            </Button>
            <Button variant="primary-filled" onClick={apply}>
              {t("Apply")}
            </Button>
          </Box>
        </Stack>
      </Popover>
    </>
  );
};

export default AmountRangeChip;
